import type { Project, ProjectFormData } from '@/types/project.types'

/** Map API status string to backend integer (PATCH /api/v1/projects/:id/status). */
export function projectStatusToApi(status: string): number | undefined {
  const map: Record<string, number> = {
    draft: 0,
    active: 1,
    completed: 2,
    cancelled: 3,
    on_hold: 4,
  }
  return map[status]
}

/** Map API `Project` to `ProjectForm` initial values (normalizes `client_id`, `type`). */
export function projectToFormInitial(project: Project): Partial<ProjectFormData> {
  const c = project.client_id
  let client_id: number | undefined
  if (c !== null && c !== undefined) {
    const n = typeof c === 'number' ? c : Number(c)
    client_id = Number.isFinite(n) ? n : undefined
  }

  const type =
    project.type !== null && project.type !== undefined && String(project.type).trim() !== ''
      ? String(project.type)
      : undefined

  return {
    title: project.title,
    title_en: project.title_en,
    client_id,
    type,
    status: project.status,
    start_date: project.start_date,
    end_date: project.end_date,
    budget: project.budget,
    description: project.description,
  }
}

function toApiDate(value: string | Date | undefined | null): string | undefined {
  if (value === undefined || value === null || value === '') return undefined
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  return String(value).slice(0, 10)
}

/** Map create form values to POST /api/v1/projects/ body. */
export function projectFormToCreatePayload(data: ProjectFormData): {
  cr_licence_id: string
  project_type: number
  location?: string
  description?: string
  title?: string
  budget?: number
  start_date?: string
  end_date?: string
} {
  const typeId = Number(data.type)
  if (!data.cr_licence_id || !Number.isFinite(typeId)) {
    throw new Error('CR licence and project type are required')
  }
  const payload: ReturnType<typeof projectFormToCreatePayload> = {
    cr_licence_id: data.cr_licence_id,
    project_type: typeId,
    location: data.location?.trim() || undefined,
    description: data.description?.trim() || undefined,
  }
  if (data.title?.trim()) payload.title = data.title.trim()
  if (data.budget !== undefined && data.budget !== null && !Number.isNaN(Number(data.budget))) {
    payload.budget = Number(data.budget)
  }
  const start = toApiDate(data.start_date)
  const end = toApiDate(data.end_date)
  if (start) payload.start_date = start
  if (end) payload.end_date = end
  return payload
}

/** Map edit form values to PUT /api/v1/projects/:id body. */
export function projectFormToUpdatePayload(
  data: Partial<ProjectFormData>,
): {
  description?: string
  project_type?: number
  title?: string
  budget?: number
  start_date?: string
  end_date?: string
} {
  const payload: ReturnType<typeof projectFormToUpdatePayload> = {}

  if (data.description !== undefined) {
    payload.description = data.description
  }

  if (data.type !== undefined && data.type !== '') {
    const typeId = Number(data.type)
    if (Number.isFinite(typeId)) {
      payload.project_type = typeId
    }
  }

  if (data.title !== undefined) {
    payload.title = data.title.trim() || undefined
  }

  if (data.budget !== undefined && data.budget !== null) {
    const budget = Number(data.budget)
    if (!Number.isNaN(budget)) payload.budget = budget
  }

  const start = toApiDate(data.start_date)
  const end = toApiDate(data.end_date)
  if (start !== undefined) payload.start_date = start
  if (end !== undefined) payload.end_date = end

  return payload
}

/** Share-target picker label: customer - name - SN - project location. */
export function formatProjectSearchLabel(
  project: Pick<Project, 'title' | 'project_number' | 'location' | 'client_name'> & {
    client?: { name?: string } | null
  },
): string {
  const customer = project.client?.name?.trim() || project.client_name?.trim() || ''
  const name = project.title?.trim() || ''
  const serial = project.project_number?.trim() || ''
  const location = project.location?.trim() || ''
  const seen = new Set<string>()
  const parts: string[] = []
  for (const part of [customer, name, serial, location]) {
    if (!part) continue
    const key = part.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    parts.push(part)
  }
  return parts.join(' - ')
}
